import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type { EvidenceFileDto, EvidenceType } from '@/features/evidence/types';
import type {
  BulkAssignWarehouseInput,
  CreateInboundLotWithPackagesResponse,
  DeletedInboundLot,
  InboundDeletePreview,
  InboundDeleteResult,
  InboundLotCreateInput,
  InboundLotDto,
  InboundLotWithLinesDto,
  InboundLotWithPackagesDetailDto,
  InboundLotWithPackagesInput,
  InboundLotWorksheetUpdateInput,
  InboundPackageCompleteInput,
  InboundPackageDeleteInput,
  InboundPackageDto,
} from './types';

export interface InboundListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface DeletedInboundLotListResult {
  data: DeletedInboundLot[];
  meta: InboundListMeta;
}

export const fetchInboundLots = async (
  batchId: string,
  params: { page?: number; per_page?: number } = {},
): Promise<{ data: InboundLotDto[]; meta: InboundListMeta }> => {
  const res = await apiClient.get<ApiSuccess<InboundLotDto[], InboundListMeta>>(
    `/monthly-batches/${batchId}/inbound-lots`,
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const fetchInboundLot = async (id: string): Promise<InboundLotWithLinesDto> => {
  const res = await apiClient.get<ApiSuccess<InboundLotWithLinesDto>>(`/inbound-lots/${id}`);
  return res.data.data;
};

/**
 * New per-package inbound-lot fetch. Returns the lot header along with
 * truck panorama photo evidence ids and the full list of packages (each
 * with weight/warehouse/photo fields nullable until the operator
 * completes them).
 */
export const fetchInboundLotWithPackages = async (
  id: string,
): Promise<InboundLotWithPackagesDetailDto> => {
  const res = await apiClient.get<ApiSuccess<InboundLotWithPackagesDetailDto>>(
    `/inbound-lots/${id}/with-packages`,
  );
  return res.data.data;
};

export const confirmInboundLot = async (id: string): Promise<void> => {
  await apiClient.post(`/inbound-lots/${id}/confirm`);
};

export const createInboundLot = async (
  batchId: string,
  body: InboundLotCreateInput,
): Promise<InboundLotDto> => {
  const res = await apiClient.post<ApiSuccess<InboundLotDto>>(
    `/monthly-batches/${batchId}/inbound-lots`,
    body,
  );
  return res.data.data;
};

/**
 * Per-package inbound flow. Backend creates the lot, the inbound_lines, and
 * links each package row to its photo evidence file in one transaction.
 */
export const createInboundLotWithPackages = async (
  batchId: string,
  body: InboundLotWithPackagesInput,
): Promise<CreateInboundLotWithPackagesResponse> => {
  const res = await apiClient.post<ApiSuccess<CreateInboundLotWithPackagesResponse>>(
    `/monthly-batches/${batchId}/inbound-lots/with-packages`,
    body,
  );
  return res.data.data;
};

export const attachInboundEvidenceFromLibrary = async (
  batchId: string,
  companyFileId: string,
  evidenceType: EvidenceType,
): Promise<EvidenceFileDto> => {
  const res = await apiClient.post<ApiSuccess<EvidenceFileDto>>(
    `/monthly-batches/${batchId}/evidence-files/from-library`,
    {
      company_file_id: companyFileId,
      evidence_type: evidenceType,
    },
  );
  return res.data.data;
};

/**
 * Complete a single pending package — fills in package_type, warehouse,
 * weights and photo evidence. Flips the package status to `completed`.
 */
export const completeInboundPackage = async (
  packageId: string,
  body: InboundPackageCompleteInput,
): Promise<InboundPackageDto> => {
  const res = await apiClient.post<ApiSuccess<InboundPackageDto>>(
    `/inbound-packages/${packageId}/complete`,
    body,
  );
  return res.data.data;
};

export const deleteInboundPackage = async (
  packageId: string,
  body: InboundPackageDeleteInput,
): Promise<void> => {
  await apiClient.post(`/inbound-packages/${packageId}/mark-deleted`, body);
};

export const deleteInboundLot = async (
  lotId: string,
  body: InboundPackageDeleteInput,
): Promise<InboundDeleteResult> => {
  const res = await apiClient.post<ApiSuccess<InboundDeleteResult>>(
    `/inbound-lots/${lotId}/cancel`,
    body,
  );
  return res.data.data;
};

export const previewInboundLotDelete = async (lotId: string): Promise<InboundDeletePreview> => {
  const res = await apiClient.get<ApiSuccess<InboundDeletePreview>>(
    `/inbound-lots/${lotId}/cancel-preview`,
  );
  return res.data.data;
};

export const fetchDeletedInboundLots = async (
  params: { monthly_batch_id?: string; page?: number; per_page?: number } = {},
): Promise<DeletedInboundLotListResult> => {
  const res = await apiClient.get<ApiSuccess<DeletedInboundLot[], InboundListMeta>>(
    '/inbound-lots/deleted',
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const restoreDeletedInboundLot = async (archiveId: string): Promise<InboundDeleteResult> => {
  const res = await apiClient.post<ApiSuccess<InboundDeleteResult>>(
    `/inbound-lots/deleted/${archiveId}/restore`,
  );
  return res.data.data;
};

/**
 * Update the lot's truck-arrival worksheet fields (車番, 計量正味,
 * 棧板數, 太空袋數, 段ボール數). `change_reason` is always required —
 * the server writes one row to `inbound_lot_revisions` with the diff.
 */
export const updateInboundLotWorksheet = async (
  lotId: string,
  body: InboundLotWorksheetUpdateInput,
): Promise<InboundLotWithPackagesDetailDto> => {
  const res = await apiClient.post<ApiSuccess<InboundLotWithPackagesDetailDto>>(
    `/inbound-lots/${lotId}/worksheet`,
    body,
  );
  return res.data.data;
};

/**
 * FR-004 Part B — tag one linked truck photo with its shot direction
 * (left/right/rear/other). Stored in the lot's gross_context; the confirm
 * gate requires left + right + rear each ≥1 once kinds are recorded.
 */
export const setTruckPhotoKind = async (
  lotId: string,
  body: { evidence_file_id: string; kind: string },
): Promise<void> => {
  await apiClient.post(`/inbound-lots/${lotId}/truck-photo-kind`, body);
};

/**
 * Atomically link a newly-uploaded evidence file to an inbound lot and store
 * its shot direction. If the call fails, the evidence file remains unlinked
 * and the lot does not gain an unclassified truck photo.
 */
export const linkTruckPhoto = async (
  lotId: string,
  body: { evidence_file_id: string; kind: string },
): Promise<void> => {
  await apiClient.post(`/inbound-lots/${lotId}/truck-photos`, body);
};

/**
 * Unlink one truck-arrival photo/video from the lot. The server keeps the
 * evidence record and underlying company-file/object; only this lot link and
 * direction metadata are removed.
 */
export const removeTruckPhoto = async (
  lotId: string,
  evidenceFileId: string,
  body: { change_reason?: string | null } = {},
): Promise<void> => {
  await apiClient.post(`/inbound-lots/${lotId}/truck-photos/${evidenceFileId}/remove`, body);
};

/**
 * Bulk-assign warehouse on a list of packages. Used by the lot detail
 * page's checkbox toolbar: select N packages → click warehouse chip.
 * The server writes one revision row per completed package whose
 * warehouse_id changes; `change_reason` is required in that case.
 */
export const bulkAssignWarehouse = async (
  lotId: string,
  body: BulkAssignWarehouseInput,
): Promise<{ updated: number }> => {
  const res = await apiClient.post<ApiSuccess<{ updated: number }>>(
    `/inbound-lots/${lotId}/packages/assign-warehouse`,
    body,
  );
  return res.data.data;
};
